import React from "react";
import styled from "styled-components";
import { LuLayers, LuRocket, LuShieldCheck } from "react-icons/lu";
import officeTwo from "../assets/officeTwo.jpg";
import {
  Container,
  Eyebrow,
  IconBox,
  Reveal,
  Section,
  SectionTitle,
} from "./ui";

const principles = [
  {
    icon: <LuLayers />,
    title: "Built to scale",
    text: "Architected to grow with your business, not just to meet today's requirements.",
  },
  {
    icon: <LuRocket />,
    title: "Concept to deployment",
    text: "The same team takes your project from the first idea all the way to production.",
  },
  {
    icon: <LuShieldCheck />,
    title: "Quality first",
    text: "Every project is delivered with precision, performance and user experience in mind.",
  },
];

const Layout = styled(Container)`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  align-items: center;
  gap: 80px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const Figure = styled(Reveal)`
  position: relative;

  img {
    width: 100%;
    aspect-ratio: 4 / 5;
    object-fit: cover;
    border-radius: 20px;
    border: 1px solid var(--color-border);
  }

  @media (max-width: 960px) {
    max-width: 560px;

    img {
      aspect-ratio: 4 / 3;
    }
  }
`;

const SinceBadge = styled.div`
  position: absolute;
  top: 24px;
  right: -24px;
  padding: 16px 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 14px;
  box-shadow: var(--shadow-lg);

  strong {
    display: block;
    font-family: var(--font-heading);
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1;
    color: var(--color-ink);
  }

  span {
    font-size: 0.8125rem;
    color: var(--color-muted);
  }

  @media (max-width: 960px) {
    right: 16px;
  }
`;

const Body = styled.p`
  font-size: 1.0625rem;
  line-height: 1.75;
  margin-top: 24px;

  strong {
    font-weight: 600;
    color: var(--color-ink);
  }
`;

const Principles = styled.ul`
  display: grid;
  gap: 24px;
  margin-top: 40px;
  padding-top: 40px;
  border-top: 1px solid var(--color-border);
`;

const Principle = styled.li`
  display: flex;
  gap: 16px;

  h3 {
    font-size: 1.0625rem;
    margin-bottom: 4px;
  }

  p {
    font-size: 0.9375rem;
    color: var(--color-muted);
  }
`;

const About = () => (
  <Section id="about">
    <Layout>
      <Figure>
        <img
          src={officeTwo}
          alt="Two Code Solutions engineers reviewing a project together"
          loading="lazy"
        />
        <SinceBadge>
          <strong>2019</strong>
          <span>Building software since</span>
        </SinceBadge>
      </Figure>

      <Reveal delay={0.1}>
        <Eyebrow>About us</Eyebrow>
        <SectionTitle>One team for web, mobile, AI and IoT</SectionTitle>
        <Body>
          We're a <strong>full-stack web &amp; mobile development team</strong>{" "}
          with deep expertise in{" "}
          <strong>AI, machine learning, IoT integration and DevOps</strong>.
          Our work connects web applications, mobile experiences, intelligent
          AI-powered systems and smart devices into products that hold
          together.
        </Body>

        <Principles>
          {principles.map(item => (
            <Principle key={item.title}>
              <IconBox aria-hidden="true">{item.icon}</IconBox>
              <div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </Principle>
          ))}
        </Principles>
      </Reveal>
    </Layout>
  </Section>
);

export default About;
